import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAnalytics, getAdminActivity, getAdminUsers, getSupervisorFellows } from '../utils/api';
import { getDashboard } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import {
  UsersIcon, PresentationChartBarIcon, CalendarDaysIcon, CheckCircleIcon,
  ShieldCheckIcon, UserCircleIcon, AcademicCapIcon, MegaphoneIcon,
  ChartBarIcon, FolderOpenIcon, BellIcon, ClockIcon,
} from '@heroicons/react/24/outline';

import DashboardPage from './DashboardPage';
import SupervisorPage from './SupervisorPage';

const MODES = [
  { key: 'admin',      label: 'Admin View',      icon: ShieldCheckIcon },
  { key: 'supervisor', label: 'Supervisor View',  icon: UsersIcon },
  { key: 'fellow',     label: 'Fellow View',      icon: UserCircleIcon },
];

const PROJECT_COLORS = {
  'Y-Check':      'bg-blue-900/60 text-blue-300',
  'Neotree':      'bg-green-900/60 text-green-300',
  'KOSHESAI':     'bg-purple-900/60 text-purple-300',
  'PROMISE':      'bg-amber-900/60 text-amber-300',
  'Fractures E3': 'bg-red-900/60 text-red-300',
  'Helen Jack':   'bg-teal-900/60 text-teal-300',
};

const ACTIVITY_ICONS = {
  presentation: PresentationChartBarIcon,
  announcement: MegaphoneIcon,
  user: UserCircleIcon,
  task: CheckCircleIcon,
};

const ACTIVITY_COLORS = {
  presentation: 'bg-purple-600/20 text-purple-400',
  announcement: 'bg-primary-600/20 text-primary-400',
  user: 'bg-green-600/20 text-green-400',
  task: 'bg-amber-600/20 text-amber-400',
};

function StatCard({ icon: Icon, label, value, color, to }) {
  const content = (
    <div className={`card flex items-center gap-4 hover:border-gray-700 transition-all ${to ? 'cursor-pointer' : ''}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value ?? '—'}</div>
        <div className="text-gray-500 text-sm">{label}</div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function AdminView() {
  const { user } = useAuth();
  const { data: analyticsData } = useQuery({ queryKey: ['analytics'], queryFn: getAnalytics });
  const { data: activityData } = useQuery({ queryKey: ['admin-activity'], queryFn: getAdminActivity });
  const { data: usersData } = useQuery({ queryKey: ['admin-users'], queryFn: getAdminUsers });

  const stats = analyticsData?.data;
  const activity = activityData?.data || [];
  const users = usersData?.data || [];
  const fellows = users.filter(u => u.role === 'fellow');
  const supervisors = users.filter(u => u.role === 'supervisor');

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {greeting}, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Admin overview — THRU Zim MSc Fellowship Platform
          </p>
        </div>
        <span className="badge bg-red-900/50 text-red-300 flex items-center gap-1.5 px-3 py-1.5">
          <ShieldCheckIcon className="w-4 h-4" /> Administrator
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={UsersIcon}              label="Total Fellows"           value={stats?.total_fellows}            color="bg-primary-600" to="/fellows" />
        <StatCard icon={PresentationChartBarIcon} label="Presentations Done"    value={stats?.presentations_completed}  color="bg-purple-600"  to="/presentations" />
        <StatCard icon={CalendarDaysIcon}        label="Upcoming Meetings"      value={stats?.meetings_upcoming}        color="bg-green-600"   to="/meetings" />
        <StatCard icon={CheckCircleIcon}         label="Tasks Completed"        value={stats?.tasks_completed}          color="bg-amber-600"   to="/tasks" />
      </div>

      {/* Quick Admin Actions */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: '/admin',         icon: ShieldCheckIcon,          label: 'Admin Panel',    sub: 'Manage users',        color: 'bg-red-600/20 text-red-400 border-red-800/50' },
            { to: '/analytics',     icon: ChartBarIcon,             label: 'Analytics',      sub: 'Charts & insights',   color: 'bg-purple-600/20 text-purple-400 border-purple-800/50' },
            { to: '/announcements', icon: MegaphoneIcon,            label: 'Announcements',  sub: 'Post to all fellows', color: 'bg-primary-600/20 text-primary-400 border-primary-800/50' },
            { to: '/resources',     icon: FolderOpenIcon,           label: 'Resources',      sub: 'Upload materials',    color: 'bg-amber-600/20 text-amber-400 border-amber-800/50' },
          ].map(({ to, icon: Icon, label, sub, color }) => (
            <Link key={to} to={to} className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl border text-center hover:opacity-80 transition-all ${color}`}>
              <Icon className="w-6 h-6" />
              <div>
                <div className="text-sm font-semibold text-white">{label}</div>
                <div className="text-xs text-gray-500">{sub}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Feed */}
        <div className="card">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <BellIcon className="w-4 h-4 text-primary-400" /> Recent Activity
          </h2>
          {activity.length === 0 ? (
            <p className="text-gray-500 text-sm">No recent activity yet.</p>
          ) : (
            <div className="space-y-3">
              {activity.map((ev, i) => {
                const Icon = ACTIVITY_ICONS[ev.type] || BellIcon;
                const color = ACTIVITY_COLORS[ev.type] || 'bg-gray-800 text-gray-400';
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-200 font-medium truncate">{ev.title}</div>
                      {ev.detail && <div className="text-xs text-gray-500 truncate">{ev.detail}</div>}
                    </div>
                    <div className="text-xs text-gray-600 flex-shrink-0 flex items-center gap-1">
                      <ClockIcon className="w-3 h-3" />
                      {formatDistanceToNow(new Date(ev.time), { addSuffix: true })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fellows Overview */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <AcademicCapIcon className="w-4 h-4 text-primary-400" /> Fellows Overview
            </h2>
            <Link to="/fellows" className="text-xs text-primary-400 hover:text-primary-300">View all</Link>
          </div>
          <div className="space-y-2">
            {fellows.slice(0, 8).map(u => (
              <Link key={u.id} to={`/fellows/${u.id}`} className="flex items-center justify-between gap-3 py-1.5 hover:opacity-80 transition-all">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-primary-600/30 flex items-center justify-center text-primary-400 text-xs font-bold flex-shrink-0">
                    {u.full_name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-gray-200 truncate font-medium">{u.full_name}</div>
                  </div>
                </div>
                <span className={`badge text-xs flex-shrink-0 ${u.is_active ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'}`}>
                  {u.is_active ? 'Active' : 'Disabled'}
                </span>
              </Link>
            ))}
            {fellows.length > 8 && (
              <Link to="/fellows" className="text-xs text-primary-400 hover:text-primary-300 block pt-1">
                +{fellows.length - 8} more fellows →
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Supervisor Summary */}
      {supervisors.length > 0 && (
        <div className="card">
          <h2 className="font-semibold text-white mb-3 flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-purple-400" /> Supervisors ({supervisors.length})
          </h2>
          <div className="flex flex-wrap gap-3">
            {supervisors.map(s => (
              <div key={s.id} className="flex items-center gap-2 bg-surface-100 rounded-lg px-3 py-2">
                <div className="w-7 h-7 rounded-full bg-purple-600/30 flex items-center justify-center text-purple-400 text-xs font-bold">
                  {s.full_name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="text-sm text-white font-medium">{s.full_name}</div>
                  <div className="text-xs text-gray-500">{s.email}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboardPage() {
  const [mode, setMode] = useState('admin');

  return (
    <div className="space-y-5">
      {/* View mode toggle */}
      <div className="flex items-center gap-2 bg-surface-100 rounded-xl p-1 w-fit border border-gray-800">
        {MODES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setMode(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              mode === key
                ? 'bg-primary-600 text-white shadow'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {mode === 'admin'      && <AdminView />}
      {mode === 'supervisor' && <SupervisorPage />}
      {mode === 'fellow'     && <DashboardPage />}
    </div>
  );
}
